/**
 * Wire-format helpers for the transactions the server builds: base64 in and out, and enough parsing to check that a
 * wallet signed without changing the message. A Solana transaction is a compact-u16 signature count, that many
 * 64-byte signatures, then the message; the message header's first byte is the number of required signatures
 * (after a 0x80 | version prefix byte for v0 messages).
 */
import bs58 from 'bs58';

/** Transactions are at most 1,232 bytes, so the spread into fromCharCode is safe. */
export const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
export const fromBase64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/** compact-u16 ("shortvec"): 1 to 3 bytes, 7 bits each, little-endian. */
export function readShortVec(bytes: Uint8Array, offset = 0): { value: number; size: number } {
  let value = 0;
  for (let i = 0; i < 3; i++) {
    const b = bytes[offset + i];
    if (b === undefined) throw new Error('Truncated transaction (signature count).');
    value |= (b & 0x7f) << (7 * i);
    if (!(b & 0x80)) return { value, size: i + 1 };
  }
  throw new Error('Malformed transaction (signature count).');
}

export interface WireTx {
  signatures: Uint8Array[];
  message: Uint8Array;
}

export function splitTransaction(bytes: Uint8Array): WireTx {
  const { value: n, size } = readShortVec(bytes);
  const start = size + 64 * n;
  if (n === 0 || bytes.length <= start) throw new Error('Malformed transaction (no message).');
  const signatures: Uint8Array[] = [];
  for (let i = 0; i < n; i++) signatures.push(bytes.subarray(size + 64 * i, size + 64 * (i + 1)));
  return { signatures, message: bytes.subarray(start) };
}

/** Signatures the message says it needs (header byte; v0 messages start with 0x80 | version). */
export function requiredSignatures(message: Uint8Array): number {
  const first = message[0] ?? 0;
  return (first & 0x80 ? message[1] : first) ?? 0;
}

const isZero = (sig: Uint8Array) => sig.every((b) => b === 0);

export function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Signature slots still empty (all zeros) among those the message requires. */
export function missingSignatures(bytes: Uint8Array): number {
  const { signatures, message } = splitTransaction(bytes);
  const need = requiredSignatures(message);
  if (need !== signatures.length) throw new Error('Malformed transaction (signature slots do not match message).');
  return signatures.slice(0, need).filter(isZero).length;
}

/** The transaction id: the first signature, base58. */
export function signatureOf(bytes: Uint8Array): string {
  const first = splitTransaction(bytes).signatures[0];
  return first && !isZero(first) ? bs58.encode(first) : '';
}
